import { Fragment } from 'react'

import Title from '@/components/title/Title'
import Text from '@/components/text/Text'
import Video from '@/components/video/Video'
import CustomImage from '@/components/image/Image'
import LinkGrid from '@/components/link-grid/LinkGrid'
import DownloadsAndLinks from '@/components/downloads-and-links/DownloadsAndLinks'
import Agenda from '@/components/agenda/Agenda'
import GoogleMap from '@/components/google-map/GoogleMap'
import DetailsGrid from '@/components/details-card/DetailsGrid'
import Itinerary from '@/components/itinerary/Itinerary'

export type RenderComponentsProps = {
  components: any[]
}

/**
 * --------------------------------------------------------------------------
 * RenderComponents
 *
 * Render a list of components based on their type
 *
 * @param {Object} components - List of components to render
 * --------------------------------------------------------------------------
 **/
export default function RenderEventComponents({ components }: RenderComponentsProps) {
  if (!components || components.length === 0) {
    return null
  }

  let downloadIndex = 1

  return Object.values(components).map((component: any, index: number) => {
    const { acf_fc_layout: componentName } = component

    const key = componentName + index
    let componentOutput

    switch (componentName) {
      case 'title':
        componentOutput = (
          <Title title={component?.title} titleTag={component?.tag} titleClass={component?.class} />
        )
        break

      case 'text':
        componentOutput = (
          <>
            <Text
              twoColumn={component?.two_column}
              heading={component?.heading}
              title={component?.title}
              firstColumnText={component?.first_column_text}
              text={component?.text}
            />

            {component?.video ? <Video video={component?.video} /> : null}
          </>
        )
        break

      case 'image':
        componentOutput = (
          <CustomImage image={component?.image} reduceSize={component?.reduce_width} lazy={true} />
        )
        break

      case 'link_grid':
        componentOutput = (
          <LinkGrid
            style={component?.style}
            links={component?.links}
            columns={component?.columns}
          />
        )
        break

      case 'downloads':
        componentOutput = (
          <DownloadsAndLinks items={component?.download_list} anchorIndex={downloadIndex++} />
        )
        break

      case 'logo_grid':
        const logoGridItems = (Array.isArray(component?.logos) ? component.logos : [])?.map(
          (item: any) => {
            return {
              image: item?.logo,
              title: item?.title,
              text: item?.text,
              link: {
                url: item?.link,
                title: 'Website',
                target: '_blank',
              },
            }
          },
        )

        componentOutput = <DetailsGrid items={logoGridItems} roundedImages={false} />
        break

      case 'media_partners':
        const mediaPartnerItems = (Array.isArray(component?.logos) ? component.logos : [])?.map(
          (item: any) => {
            return {
              image: item?.logo,
              title: item?.title,
              text: item?.text,
              link: {
                url: item?.link,
                title: 'Website',
                target: '_blank',
              },
            }
          },
        )

        componentOutput = <DetailsGrid items={mediaPartnerItems} roundedImages={false} />
        break

      case 'event_partners':
        const eventPartnerItems = (Array.isArray(component?.logos) ? component.logos : [])?.map(
          (item: any) => {
            return {
              image: item?.logo,
              title: item?.title,
              text: item?.text,
              link: {
                url: item?.link,
                title: 'Website',
                target: '_blank',
              },
            }
          },
        )

        componentOutput = <DetailsGrid items={eventPartnerItems} />
        break

      case 'people':
        const peopleItems = (Array.isArray(component?.people) ? component.people : [])?.map(
          (item: any) => {
            return {
              image: item?.image,
              title: item?.name,
              subtitle: item?.job_title,
              text: item?.bio,
              link: {
                url: item?.link,
                title: 'Profile',
              },
            }
          },
        )

        componentOutput = <DetailsGrid items={peopleItems} />
        break

      case 'contacts':
        const contactItems = (Array.isArray(component?.people) ? component.people : [])?.map(
          (item: any) => {
            return {
              image: item?.image,
              title: item?.name,
              subtitle: item?.job_title,
              text: item?.bio,
              link: {
                url: item?.link,
                title: 'Profile',
              },
            }
          },
        )

        componentOutput = <DetailsGrid items={contactItems} />
        break

      case 'itinerary':
        componentOutput = <Itinerary events={component?.events} />
        break

      case 'agenda':
        componentOutput = <Agenda {...component} />
        break

      case 'google_map':
        const { address: mapAddress, lat: mapLat, lng: mapLng } = component?.google_map

        componentOutput = <GoogleMap address={mapAddress} lat={mapLat} lng={mapLng} />

        break
    }

    return <Fragment key={key}>{componentOutput}</Fragment>
  })
}
